import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Bot, Send, User, Loader2, Plus, Database, Sparkles, Globe } from 'lucide-react';
import { SohbetKapsam, EMPTY_SCOPE } from './SohbetKapsam';
import { PdfButton } from './PdfButton';
import { exportSohbetPdf } from '../../utils/ekipmanZekasiPdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HIZLI = [
  'Hangi jeneratörler risk altında?',
  'Değişim önerilen ekipmanlar ve gerekçeleri neler?',
  'Kapanmamış güvenlik eksikliği olan ekipmanları listele',
  'En düşük sağlık skoruna sahip 5 ekipman hangileri?',
  'Filo deseni uyarısı var mı, hangi marka/model?',
];
const HIZLI_KAPSAM = [
  'Bu kapsamdaki ekipmanların genel durumu nasıl, öncelikli riskler neler?',
  'Bu kapsamda değişim önerilen ekipman var mı, gerekçesi ne?',
  'Kapsamdaki ekipmanlar için önümüzdeki 12 ay bakım planı öner',
  'Bu kapsamda kontrol süresi geçmiş veya yaklaşan ekipmanlar hangileri?',
];
const HIZLI_EKIPMAN = [
  'Bu ekipmanın mevcut sağlık durumu ve en kritik bulguları neler?',
  'Bu ekipman onarılmalı mı değiştirilmeli mi, gerekçesiyle açıkla',
  'Son arıza ve kontrol geçmişine göre tekrar eden sorun var mı?',
  'Sonraki periyodik kontrolde nelere özellikle bakılmalı?',
  'Bu ekipman için 12 aylık maliyet öngörüsü ve bütçe önerisi ne?',
];
const HIZLI_GENEL = [
  'Asansör periyodik kontrol periyodu ve yasal dayanağı nedir?',
  'Jeneratör yük testi hangi sıklıkla ve nasıl yapılmalı?',
  'Yangın algılama sistemlerinde yıllık bakım kapsamı (TS EN 54) nedir?',
  'UPS akü ömrü nasıl değerlendirilir, ne zaman değiştirilmeli?',
  'Kırmızı etiket alan bir ekipmanda yasal süreç nasıl işler?',
];
const MD = 'text-xs leading-relaxed [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:my-0.5 [&_strong]:text-white [&_h1]:text-sm [&_h1]:font-bold [&_h1]:text-[#E67E22] [&_h2]:text-sm [&_h2]:font-bold [&_h2]:text-[#E67E22] [&_h3]:text-xs [&_h3]:font-bold [&_table]:text-[11px] [&_table]:w-full [&_table]:my-1 [&_th]:border [&_th]:border-gray-700 [&_th]:px-1.5 [&_th]:py-0.5 [&_th]:bg-gray-900/60 [&_td]:border [&_td]:border-gray-700 [&_td]:px-1.5 [&_td]:py-0.5 [&_hr]:border-gray-700 [&_hr]:my-2';

/** Ekipman Zekâsı — Uzman Asistan sohbeti (kayıtlı analizler + filo verisi) */
export default function EkipmanZekasiSohbet() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [scope, setScope] = useState({ ...EMPTY_SCOPE });
  const [options, setOptions] = useState(null);
  const [lastModel, setLastModel] = useState('');
  const endRef = useRef(null);

  useEffect(() => { loadSessions(); loadOptions(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadSessions = async () => {
    try { const r = await axios.get(`${API}/ai/chat/sessions`, { withCredentials: true }); setSessions(r.data); } catch (e) { /* ignore */ }
  };
  const loadOptions = async () => {
    try { const r = await axios.get(`${API}/ai/chat/scope-options`, { withCredentials: true }); setOptions(r.data); } catch (e) { /* ignore */ }
  };
  const openSession = async (sid) => {
    try {
      const r = await axios.get(`${API}/ai/chat/history/${sid}`, { withCredentials: true });
      setSessionId(sid);
      setMessages(r.data.flatMap(m => [{ role: 'user', content: m.user_message, kapsam: m.kaynaklar?.kapsam }, { role: 'ai', content: m.ai_response, kaynaklar: m.kaynaklar }]));
      const last = r.data[r.data.length - 1];
      if (last?.model) setLastModel(last.model);
      if (last?.kapsam) setScope({ ...EMPTY_SCOPE, ...last.kapsam });
    } catch (e) { toast.error('Sohbet yüklenemedi'); }
  };
  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai/chat`, { message: msg, session_id: sessionId, scope }, { withCredentials: true });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { role: 'ai', content: r.data.response, kaynaklar: r.data.kaynaklar }]);
      if (r.data.model) setLastModel(r.data.model);
      loadSessions();
    } catch (e) {
      setMessages(m => [...m, { role: 'ai', error: true, content: e.response?.data?.detail || 'Yanıt alınamadı' }]);
    } finally { setLoading(false); }
  };

  const isGenel = scope.mode === 'genel';
  const hasScope = !isGenel && (scope.tesis_turu || scope.ekipman_tipi || scope.bina_id);
  const hizli = isGenel ? HIZLI_GENEL : scope.equipment_id ? HIZLI_EKIPMAN : hasScope ? HIZLI_KAPSAM : HIZLI;
  const seciliEkipman = scope.equipment_id ? (options?.ekipmanlar || []).find(e => e.id === scope.equipment_id) : null;
  const placeholder = isGenel ? 'Örn: Asansör periyodik kontrolünde kırmızı etiket kriterleri nelerdir?'
    : seciliEkipman ? `${seciliEkipman.seri_no} hakkında sorun — örn: son arızaları ve değişim gerekçesi?`
    : hasScope ? 'Seçili kapsamdaki ekipmanlar hakkında sorun…' : 'Örn: X binasındaki asansörün değişim gerekçesi ne?';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-3" data-testid="ez-sohbet">
      <div className="lg:col-span-3 rounded-lg border border-gray-800 bg-[#1A2332] flex flex-col h-[640px]">
        <div className="px-4 py-2.5 border-b border-gray-800 flex items-center gap-2 flex-wrap">
          <Bot className="w-4 h-4 text-[#E67E22]" />
          <span className="text-sm font-semibold text-white">Uzman Asistan</span>
          {isGenel
            ? <span className="text-[10px] text-cyan-300 bg-cyan-900/30 border border-cyan-800/40 rounded-full px-2 py-0.5 flex items-center gap-1" data-testid="ez-mod-rozet"><Globe className="w-3 h-3" /> Genel soru modu</span>
            : <span className="text-[10px] text-emerald-300 bg-emerald-900/30 border border-emerald-800/40 rounded-full px-2 py-0.5 flex items-center gap-1" data-testid="ez-mod-rozet"><Database className="w-3 h-3" /> Filo verileri + kayıtlı analizler bağlı</span>}
          <div className="ml-auto flex items-center gap-1.5">
            <PdfButton testId="ez-sohbet-pdf" disabled={messages.length === 0 || loading}
              onExport={(pageSize) => exportSohbetPdf({ messages, kapsam: [...messages].reverse().find(m => m.kaynaklar)?.kaynaklar?.kapsam || (isGenel ? 'Genel soru' : 'Tüm filo'), model: lastModel, pageSize })} />
            <Button variant="ghost" size="sm" className="h-7 text-gray-400 hover:text-white" onClick={() => { setSessionId(null); setMessages([]); }} data-testid="ez-sohbet-yeni"><Plus className="w-3.5 h-3.5 mr-1" /> Yeni</Button>
          </div>
        </div>
        <SohbetKapsam scope={scope} onChange={setScope} options={options} disabled={loading} />
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <Sparkles className="w-10 h-10 text-gray-700 mx-auto mb-2" />
              <p className="text-sm text-gray-400">
                {isGenel ? 'Genel teknik / mevzuat sorusu sorun — filo verisi kullanılmaz.'
                  : seciliEkipman ? `${seciliEkipman.seri_no} (${seciliEkipman.alt_tip || seciliEkipman.ekipman_tipi}) için kontrol raporları, arızalar ve uzman analizleri bağlandı.`
                  : hasScope ? 'Seçili kapsamdaki ekipmanlar ve analizleri üzerinden soru sorun.'
                  : 'Filoya soru sorun — yanıtlar kayıtlı uzman analizleri ve ekipman verisi üzerinden verilir.'}
              </p>
              <div className="flex flex-wrap gap-2 justify-center mt-4 max-w-xl mx-auto">
                {hizli.map(q => <button key={q} onClick={() => send(q)} className="text-[11px] px-2.5 py-1.5 rounded-full border border-gray-700 text-gray-300 hover:border-[#E67E22] hover:text-white" data-testid="ez-hizli-soru">{q}</button>)}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : ''}`}>
              {m.role === 'ai' && <div className="w-7 h-7 rounded-full bg-[#E67E22]/20 flex items-center justify-center flex-shrink-0"><Bot className="w-4 h-4 text-[#E67E22]" /></div>}
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${m.role === 'user' ? 'bg-[#1e3a5f] text-white text-xs' : m.error ? 'bg-red-900/30 text-red-300 text-xs border border-red-800/30' : 'bg-gray-800/80 text-gray-200'}`} data-testid={m.role === 'ai' ? 'ez-ai-mesaj' : 'ez-user-mesaj'}>
                {m.role === 'ai' && !m.error ? <div className={MD}><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div> : <div className="whitespace-pre-wrap">{m.content}</div>}
                {m.kaynaklar && (
                  <div className="mt-1.5 pt-1.5 border-t border-gray-700/50 text-[10px] text-cyan-400/80" data-testid="ez-kaynak">
                    {m.kaynaklar.mod === 'genel' ? 'Genel bilgi yanıtı — filo verisi kullanılmadı' : <>Kapsam: {m.kaynaklar.kapsam || 'Tüm filo'} · {m.kaynaklar.ekipman} ekipman · {m.kaynaklar.analizli} analiz · {m.kaynaklar.detay} detaylı analiz · {m.kaynaklar.eksiklik} açık eksiklik</>}
                  </div>
                )}
              </div>
              {m.role === 'user' && <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0"><User className="w-4 h-4 text-gray-300" /></div>}
            </div>
          ))}
          {loading && <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> {isGenel ? 'Uzman asistan yanıt hazırlıyor…' : 'Uzman asistan kapsamdaki verileri inceliyor…'}</div>}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-gray-800 flex gap-2">
          <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder={placeholder} className="bg-gray-800 border-gray-700 text-white h-9 text-sm" data-testid="ez-sohbet-input" />
          <Button onClick={() => send()} disabled={loading || !input.trim()} className="bg-[#E67E22] hover:bg-[#d3701c] text-white h-9" data-testid="ez-sohbet-gonder"><Send className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="rounded-lg border border-gray-800 bg-[#1A2332] p-3 h-[640px] overflow-y-auto">
        <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Geçmiş Sohbetler</p>
        {sessions.length === 0 && <p className="text-xs text-gray-600">Henüz sohbet yok</p>}
        {sessions.map(s => (
          <button key={s.session_id} onClick={() => openSession(s.session_id)} className={`w-full text-left text-xs p-2 rounded mb-1 border ${sessionId === s.session_id ? 'border-[#E67E22]/60 bg-[#E67E22]/10 text-white' : 'border-gray-800 text-gray-300 hover:bg-gray-800/60'}`} data-testid="ez-sohbet-oturum">
            <p className="truncate">{s.last_message}</p>
            <p className="text-[10px] text-gray-500">{new Date(s.last_at).toLocaleString('tr-TR')} · {s.n} mesaj</p>
          </button>
        ))}
      </div>
    </div>
  );
}
